import { useIsMobile } from "@core/shared/hooks/useIsMobile";
import { URLS } from "@core/shared/utils";
import { isImmersiveChallengeRoute } from "@core/shared/utils/immersive-routes";
import { BaseLink } from "@design-system/components/link";
import { settingsAtom } from "@framework/shared/atoms";
import { trans, plainTrans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import packageJson from "package.json";
import {
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaMapMarkerAlt,
  FaPhone,
  FaPinterest,
  FaSnapchat,
  FaTiktok,
  FaTwitter,
  FaWhatsapp,
  FaYoutube,
} from "react-icons/fa";
import { FooterPaymentMethods } from "./FooterPaymentMethods";

export function Footer() {
  const settings = settingsAtom.useValue();
  const { general, social, contact } = settings;
  const isMobile = useIsMobile();
  const location = useLocation();

  if (location.search.includes("noLayout")) {
    return null;
  }

  if (isImmersiveChallengeRoute(location.pathname)) {
    return null;
  }

  if (isMobile) {
    return <FooterMobile settings={settings} />;
  }

  return (
    <footer className="bg-gray-100 transition-colors duration-300 dark:bg-gray-900">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* About Section */}
          <div className="space-y-4">
            {general?.footerLogo?.url ? (
              <img
                src={general?.footerLogo?.url}
                alt={general?.appName}
                className="h-12 w-auto"
              />
            ) : general?.logo?.url ? (
              <img
                src={general?.logo?.url}
                alt={general?.appName}
                className="h-12 w-auto"
              />
            ) : (
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {general?.appName}
              </h2>
            )}
            {general?.description && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {general.description}
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {general?.brief}
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-2 rtl:gap-2">
              {social?.facebook && (
                <a
                  title={plainTrans("footer.facebook")}
                  href={social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                >
                  <FaFacebook size={20} />
                </a>
              )}
              {social?.instagram && (
                <a
                  title={plainTrans("footer.instagram")}
                  href={social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-pink-600 dark:text-gray-400 dark:hover:text-pink-400"
                >
                  <FaInstagram size={20} />
                </a>
              )}
              {social?.tiktok && (
                <a
                  title={plainTrans("footer.tiktok")}
                  href={social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                >
                  <FaTiktok size={20} />
                </a>
              )}

              {social?.twitter && (
                <a
                  title={plainTrans("footer.twitter")}
                  href={social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-blue-400 dark:text-gray-400 dark:hover:text-blue-300"
                >
                  <FaTwitter size={20} />
                </a>
              )}
              {social?.youtube && (
                <a
                  title={plainTrans("footer.youtube")}
                  href={social.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                >
                  <FaYoutube size={20} />
                </a>
              )}
              {social?.linkedIn && (
                <a
                  title={plainTrans("footer.linkedIn")}
                  href={social.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-blue-700 dark:text-gray-400 dark:hover:text-blue-500"
                >
                  <FaLinkedin size={20} />
                </a>
              )}
              {social?.snapchat && (
                <a
                  title={plainTrans("footer.snapchat")}
                  href={social.snapchat}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-yellow-400 dark:text-gray-400 dark:hover:text-yellow-300"
                >
                  <FaSnapchat size={20} />
                </a>
              )}
              {social?.pinterest && (
                <a
                  title={plainTrans("footer.pinterest")}
                  href={social.pinterest}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 transition-colors duration-300 hover:text-red-700 dark:text-gray-400 dark:hover:text-red-500"
                >
                  <FaPinterest size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {trans("footer.quickLinks")}
            </h3>
            <ul className="space-y-2">
              <li>
                <BaseLink to={URLS.aboutUs} title={plainTrans("footer.aboutUs")}>
                  {trans("footer.aboutUs")}
                </BaseLink>
              </li>
              <li>
                <BaseLink to={URLS.contactUs} title={plainTrans("footer.contactUs")}>
                  {trans("footer.contactUs")}
                </BaseLink>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {trans("footer.customerService")}
            </h3>
            <ul className="space-y-2">
              <li>
                <BaseLink
                  to={URLS.termsConditions}
                  title={plainTrans("footer.termsConditions")}
                >
                  {trans("footer.termsConditions")}
                </BaseLink>
              </li>
              {/* <li>
                <BaseLink
                  to={URLS.pages.privacyPolicy}
                  title={plainTrans("footer.privacyPolicy")}
                >
                  {trans("footer.privacyPolicy")}
                </BaseLink>
              </li> */}
              <li>
                <BaseLink
                  to={URLS.returnPolicy}
                  title={plainTrans("footer.returnPolicy")}
                >
                  {trans("footer.returnPolicy")}
                </BaseLink>
              </li>
              {/* <li>
                <BaseLink to={URLS.faq} title={plainTrans("footer.faq")}>
                  {trans("footer.faq")}
                </BaseLink>
              </li> */}
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {trans("footer.contactUs")}
            </h3>
            <ul className="space-y-3">
              {contact?.address && (
                <li className="flex items-start space-x-3 rtl:space-x-reverse">
                  <FaMapMarkerAlt className="mt-1 text-gray-600 dark:text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">
                    {contact.address}
                  </span>
                </li>
              )}
              {contact?.phoneNumber && (
                <li className="flex items-center space-x-3 rtl:space-x-reverse">
                  <FaPhone className="text-gray-600 dark:text-gray-400" />
                  <a
                    title={plainTrans("footer.phoneNumber")}
                    href={`tel:${contact.phoneNumber}`}
                    className="text-gray-600 hover:text-primary dark:text-gray-400"
                  >
                    {contact.phoneNumber}
                  </a>
                </li>
              )}
              {contact?.whatsappNumber && (
                <li className="flex items-center space-x-3 rtl:space-x-reverse">
                  <FaWhatsapp className="text-gray-600 dark:text-gray-400" />
                  <a
                    title={plainTrans("footer.whatsapp")}
                    href={`https://wa.me/${contact.whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-primary dark:text-gray-400"
                  >
                    {contact.whatsappNumber}
                  </a>
                </li>
              )}
              {contact?.email && (
                <li className="flex items-center space-x-3 rtl:space-x-reverse">
                  <FaEnvelope className="text-gray-600 dark:text-gray-400" />
                  <a
                    title={plainTrans("footer.email")}
                    href={`mailto:${contact.email}`}
                    className="text-gray-600 hover:text-primary dark:text-gray-400"
                  >
                    {contact.email}
                  </a>
                </li>
              )}
              {contact?.workingDays && (
                <li className="text-gray-600 dark:text-gray-400">
                  <strong>{trans("footer.workingHours")}:</strong>{" "}
                  {contact.workingDays}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-200 dark:border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div className="flex flex-col items-center space-y-1 sm:items-start rtl:sm:items-end">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                © {new Date().getFullYear()} {general?.appName}.{" "}
                {trans("footer.allRightsReserved")}
              </p>
              {/* Version */}
              <div className="flex items-center gap-2 rtl:gap-2">
                <div className="text-xs text-gray-400 dark:text-gray-400">
                  v{packageJson.version}
                </div>
                <BaseLink
                  to="http://facebook.com/mentoor.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 dark:text-gray-400"
                >
                  {trans("poweredBy")}
                </BaseLink>
              </div>
            </div>
            {/* Payment Methods */}
            <div className="flex items-center">
              <FooterPaymentMethods />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Mobile Footer Component - Compact Version
function FooterMobile({ settings }: { settings: any }) {
  const { general, social, contact } = settings;

  return (
    <footer className="bg-gray-100 pb-20 transition-colors duration-300 dark:bg-gray-900 lg:pb-4">
      {/* Compact Main Footer */}
      <div className="container mx-auto px-4 py-4">
        {/* Brand */}
        <div className="mb-4 text-center">
          {general?.footerLogo?.url ? (
            <img
              src={general?.footerLogo?.url}
              alt={general?.appName}
              className="mx-auto mb-3 h-8 w-auto"
            />
          ) : general?.logo?.url ? (
            <img
              src={general?.logo?.url}
              alt={general?.appName}
              className="mx-auto mb-3 h-8 w-auto"
            />
          ) : (
            <h2 className="mb-3 text-lg font-bold text-gray-900 dark:text-gray-100">
              {general?.appName}
            </h2>
          )}
        </div>

        {/* Quick Links - Horizontal Compact */}
        <div className="mb-4">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm">
            <BaseLink
              to={URLS.aboutUs}
              title={plainTrans("footer.aboutUs")}
              className="text-gray-600 hover:text-primary dark:text-gray-400"
            >
              {trans("footer.aboutUs")}
            </BaseLink>
            <span className="text-gray-400">•</span>
            <BaseLink
              to={URLS.contactUs}
              title={plainTrans("footer.contactUs")}
              className="text-gray-600 hover:text-primary dark:text-gray-400"
            >
              {trans("footer.contactUs")}
            </BaseLink>
            <span className="text-gray-400">•</span>
            <BaseLink
              to={URLS.termsConditions}
              title={plainTrans("footer.termsConditions")}
              className="text-gray-600 hover:text-primary dark:text-gray-400"
            >
              {trans("footer.termsConditions")}
            </BaseLink>
            <span className="text-gray-400">•</span>
            <BaseLink
              to={URLS.returnPolicy}
              title={plainTrans("footer.returnPolicy")}
              className="text-gray-600 hover:text-primary dark:text-gray-400"
            >
              {trans("footer.returnPolicy")}
            </BaseLink>
          </div>
        </div>

        {/* Contact & Social - Combined with Better RTL Support */}
        <div className="mb-4 text-center">
          {/* Contact Info */}
          {(contact?.phoneNumber || contact?.email) && (
            <div className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
              {contact?.phoneNumber && (
                <a
                  title={plainTrans("footer.phoneNumber")}
                  href={`tel:${contact.phoneNumber}`}
                  className="flex items-center hover:text-primary"
                >
                  <FaPhone className="ml-2 rtl:ml-0 rtl:mr-2" size={10} />
                  {contact.phoneNumber}
                </a>
              )}
              {contact?.email && (
                <a
                  title={plainTrans("footer.email")}
                  href={`mailto:${contact.email}`}
                  className="flex items-center hover:text-primary"
                >
                  <FaEnvelope className="ml-2 rtl:ml-0 rtl:mr-2" size={10} />
                  {contact.email}
                </a>
              )}
            </div>
          )}

          {/* Social Media Links - Below Contact with Reduced Spacing */}
          {social && Object.values(social).some(Boolean) && (
            <div className="flex items-center justify-center gap-2">
              {social?.facebook && (
                <a
                  title={plainTrans("footer.facebook")}
                  href={social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white p-1 text-gray-600 shadow-sm transition-colors duration-300 hover:text-blue-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-blue-400"
                >
                  <FaFacebook size={12} />
                </a>
              )}
              {social?.instagram && (
                <a
                  title={plainTrans("footer.instagram")}
                  href={social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white p-1 text-gray-600 shadow-sm transition-colors duration-300 hover:text-pink-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-pink-400"
                >
                  <FaInstagram size={12} />
                </a>
              )}
              {social?.twitter && (
                <a
                  title={plainTrans("footer.twitter")}
                  href={social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white p-1 text-gray-600 shadow-sm transition-colors duration-300 hover:text-blue-400 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-blue-300"
                >
                  <FaTwitter size={12} />
                </a>
              )}
              {social?.youtube && (
                <a
                  title={plainTrans("footer.youtube")}
                  href={social.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white p-1 text-gray-600 shadow-sm transition-colors duration-300 hover:text-red-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-red-400"
                >
                  <FaYoutube size={12} />
                </a>
              )}
              {social?.whatsapp && (
                <a
                  title={plainTrans("footer.whatsapp")}
                  href={social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white p-1 text-gray-600 shadow-sm transition-colors duration-300 hover:text-green-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-green-400"
                >
                  <FaWhatsapp size={12} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Compact Bottom Footer */}
      <div className="border-t border-gray-200 dark:border-gray-800">
        <div className="container mx-auto px-4 py-3">
          <div className="text-center">
            {/* Copyright */}
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} {general?.appName}.{" "}
              {trans("footer.allRightsReserved")}
            </p>

            {/* Version and Powered By - Inline */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-400">
              <span>v{packageJson.version}</span>
              <span>•</span>
              <BaseLink
                to="http://facebook.com/mentoor.me"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary"
              >
                {trans("poweredBy")}
              </BaseLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
